import { GoogleGenAI, Type } from "@google/genai";
import { ToolResult } from "../types";

// Support both standard Node env and React App env variables
const apiKey = process.env.API_KEY || process.env.REACT_APP_API_KEY || ''; 

const ai = new GoogleGenAI({ apiKey });

export const findTools = async (query: string, filterModifier: string = ''): Promise<ToolResult[]> => {
  if (!apiKey) {
    console.warn("No API Key found. Returning mock data.");
    // Fallback Mock Data for Demo purposes
    return [
      {
        name: "Remove.bg",
        url: "https://www.remove.bg",
        description: "The industry standard for removing image backgrounds automatically. Just drag and drop, AI handles the rest in seconds.",
        pricing: "Freemium",
        matchReason: "It's the fastest tool for this specific task without requiring an account.",
        trustScore: 98,
        activeUsers: "15M+",
        category: "Image Editing",
        pros: ["1-click automated removal", "High precision on hair/fur", "API available"],
        cons: ["Free version limits resolution", "Credits system for bulk"],
        platforms: ["Web", "Win", "Mac", "Linux"],
        alternativeTo: "Adobe Photoshop",
        hasDeal: true,
        dealExpiry: "2h left",
        lastUpdated: "2 days ago",
        reviews: 1240,
        editorLoveNote: "I use this daily. It's the only one that handles hair strands correctly without manual masking.",
        warning: "Free download is low-res (0.25MP). HD requires credits.",
        specs: { watermark: false, exportLimit: "1 HD free", requiresCard: false }
      },
      {
        name: "TinyWow",
        url: "https://tinywow.com",
        description: "A completely free collection of tools for PDF, video, and image manipulation. No hourly limits or watermarks.",
        pricing: "Free",
        matchReason: "Perfect if you need a free solution without sign-up friction.",
        trustScore: 94,
        activeUsers: "2M+",
        category: "Utilities",
        pros: ["100% Free", "No watermarks", "Files deleted after 1h"],
        cons: ["Ads on the site", "Slower processing for large files"],
        platforms: ["Web"],
        alternativeTo: "Adobe Acrobat",
        lastUpdated: "1 week ago",
        reviews: 850,
        editorLoveNote: "Ugly interface, but it's the Swiss Army Knife of the web. Saves me $30/mo on Adobe subs.",
        specs: { watermark: false, exportLimit: "Unlimited", requiresCard: false }
      },
      {
        name: "Photopea",
        url: "https://www.photopea.com",
        description: "Advanced image editor supporting PSD, XCF, XD and CDR formats. It is a free online alternative to Adobe Photoshop.",
        pricing: "Free",
        matchReason: "Full-featured editor running directly in the browser.",
        trustScore: 96,
        activeUsers: "5M+",
        category: "Design",
        pros: ["Supports PSD files", "Layer support", "No install needed"],
        cons: ["Ads in sidebar", "Performance depends on browser"],
        platforms: ["Web"],
        alternativeTo: "Photoshop",
        lastUpdated: "3 days ago",
        reviews: 3200,
        warning: "Can lag on old laptops if opening large PSDs.",
        specs: { watermark: false, exportLimit: "Unlimited", requiresCard: false }
      }
    ];
  }

  try {
    // Highly optimized prompt for speed + psychological triggers
    const prompt = `Task: Find 3 web tools for: "${query}".
    Constraint: ${filterModifier || 'Quality & Ease of use.'}
    
    CRITICAL: Act as a cynical, expert editor. 
    1. 'editorLoveNote': Write a personal, 1-sentence opinion on WHY this tool rocks (e.g. "Blazing fast export").
    2. 'warning': If there is a hidden catch (e.g. "Watermark on free plan", "Requires Credit Card", "Slow"), list it brutally. If none, leave empty.
    
    Output JSON Array with 3 items.
    Fields: name, url, description, pricing (Free/Freemium/Paid), category, pros (2 short strings), cons (1 short string), alternativeTo (famous tool), hasDeal (boolean, random), dealExpiry (string, e.g. "4h left"), matchReason (short sentence), trustScore (80-99), platforms (array strings), editorLoveNote (string), warning (string or null), specs (object: watermark:boolean, exportLimit:string, requiresCard:boolean).
    
    Strictly JSON only.`;

    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        // Using a schema helps the model respond faster and more accurately
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              url: { type: Type.STRING },
              description: { type: Type.STRING },
              pricing: { type: Type.STRING, enum: ["Free", "Freemium", "Paid", "Open Source"] },
              matchReason: { type: Type.STRING },
              trustScore: { type: Type.INTEGER },
              activeUsers: { type: Type.STRING },
              category: { type: Type.STRING },
              pros: { type: Type.ARRAY, items: { type: Type.STRING } },
              cons: { type: Type.ARRAY, items: { type: Type.STRING } },
              platforms: { type: Type.ARRAY, items: { type: Type.STRING } },
              alternativeTo: { type: Type.STRING },
              hasDeal: { type: Type.BOOLEAN },
              dealExpiry: { type: Type.STRING },
              lastUpdated: { type: Type.STRING },
              reviews: { type: Type.INTEGER },
              editorLoveNote: { type: Type.STRING },
              warning: { type: Type.STRING, nullable: true },
              specs: {
                type: Type.OBJECT,
                properties: {
                  watermark: { type: Type.BOOLEAN },
                  exportLimit: { type: Type.STRING },
                  requiresCard: { type: Type.BOOLEAN }
                },
                nullable: true
              }
            },
            required: ["name", "url", "description", "pricing", "matchReason", "trustScore", "category", "pros", "cons", "platforms"],
          },
        },
      },
    });

    const text = response.text;
    if (!text) return [];
    return JSON.parse(text) as ToolResult[];
  } catch (error) {
    console.error("Error fetching tools:", error);
    return [];
  }
};